<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import type { PersonMonsterType } from '@/types/monster'
import type { DailyChallengeModeType } from '@/types/user'
import { formatMs } from '@/utils'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import { nextTick } from 'vue'
import { computed, ref, watch } from 'vue'
import type { FightResType } from '@/types/fight'
import { fightExplain } from '@/core/fight'
import FightSkillInfo from '@/views/fight/components/FightSkillInfo.vue'

const commonStore = useCommonStore()
const userStore = useUserStore()

const props = defineProps<{ monster: PersonMonsterType | undefined; monsterCurHp: number; useTime?: number }>()
const emits = defineEmits(['fightOver'])

const monsterHp = ref(0)
const monsterCurHp = ref(0)
const monsterCurHpPercent = computed(() => (monsterCurHp.value / monsterHp.value) * 100)
const fightMessageList = ref<string[]>([])
const curMp = ref(userStore.userAttr.mp)
const curCdList = ref<[number, number][]>([])

const overTime = ref(0)

watch(
  () => props.monster,
  (val) => {
    if (!val) return
    isFail.value = false
    monsterHp.value = val.hp
    monsterCurHp.value = props.monsterCurHp || val.hp
    fightMessageList.value = []
    if (['gold', 'equip', 'diamond'].includes(props.monster!.type)) {
      overTime.value = 30000
    } else {
      overTime.value = 180000 - (props?.useTime || 0)
    }
  },
)
function fightOver(res: 'fail' | 'success') {
  if (res === 'fail') {
    overTime.value = 0
    ElMessage.error('您的个人挑战失败')
  } else {
    commonStore.getMailList()
    if (!props.monster) return
    const { type } = props.monster
    if (['gold', 'diamond', 'equip'].includes(type)) {
      userStore.dailyChallengeTimes[type as DailyChallengeModeType] -= 1
    }
    ElMessage.success('您的个人挑战成功，请在邮件处领取奖励')
    emits('fightOver', 'success')
  }
}

const isFail = ref(false)
async function cancelFight() {
  await socketEmitAsync({ type: 'cancelPersonFight' })
  emits('fightOver', 'fail')
}

watch(
  () => commonStore.isSocketInit,
  async (val) => {
    if (!val) return
    socketOn({
      type: 'personFightCore',
      callBack: (res: { monsterCurHp: number }) => {
        monsterCurHp.value = res.monsterCurHp
      },
    })
    socketOn({
      type: 'personFightRes',
      callBack: async (res: FightResType) => {
        if (['gold', 'equip', 'diamond'].includes(props.monster!.type)) {
          overTime.value = 30000 - res.useTime
        } else {
          overTime.value = 180000 - res.useTime
        }
        const fightText = fightExplain(res.atkInfoList, res.element)
        fightMessageList.value.push(fightText)
        const length = fightMessageList.value.length
        if (length > 60) {
          fightMessageList.value = fightMessageList.value.slice(Math.max(length - 30, 0))
        }
        curMp.value = res.mp
        curCdList.value = res.cdList
        const messageBoxEl = document.getElementById('personMessageBox')
        await nextTick()
        if (!messageBoxEl) return
        messageBoxEl.scrollTo({
          top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
          behavior: 'smooth',
        })
      },
    })
    socketOn({
      type: 'personFightSuc',
      callBack: (res: Record<DailyChallengeModeType, number>) => {
        fightMessageList.value.push('挑战成功')
        if (['gold', 'diamond', 'equip'].includes(props.monster?.type || '')) {
          userStore.dailyChallengeCount = res
        } else if (props.monster?.type === 'runeTower') {
          userStore.runeTowerLevel++
          userStore.runeTowerTimes = 10
        } else if (props.monster?.type === 'eternalTower') {
          userStore.eternalTowerLevel++
          userStore.eternalTowerTimes = 10
        }
        fightOver('success')
      },
    })
    socketOn({
      type: 'personFightFail',
      callBack: () => {
        fightMessageList.value.push('挑战失败')
        isFail.value = true
        fightOver('fail')
      },
    })
  },
)
</script>

<template>
  <div class="person-fight">
    <div class="monster-info">
      <MonsterDetail :monster="monster" icon="♦️" />
      <el-progress :text-inside="true" :stroke-width="20" :percentage="(monsterCurHpPercent < 0 ? 0 : monsterCurHpPercent) || 0" status="exception">
        <span>{{ monster && monsterCurHpPercent ? monsterCurHpPercent.toFixed(2) : 0 }} %</span>
      </el-progress>
      <p class="monster-extra-info" style="text-align: right">
        <span>{{ monsterCurHp }} / {{ monsterHp }}</span>
      </p>
    </div>
    <div class="fight-over-timer">
      {{ formatMs(overTime) }}
    </div>
    <div class="fight-res-info-wrap">
      <h4>战斗信息</h4>
      <div class="fight-res-info affix" id="personMessageBox">
        <p v-for="(item, index) of fightMessageList" :key="index" v-html="item"></p>
      </div>
      <FightSkillInfo :mp="curMp" :cd-list="curCdList" v-if="userStore.soulType > 3" />
    </div>
    <div class="fight-btn-wrap">
      <el-button class="main" type="danger" plain v-show="monsterCurHp !== 0 && !isFail" @click="cancelFight">取消挑战</el-button>
      <el-button class="main" type="success" plain v-show="monsterCurHp !== 0 && isFail" @click="emits('fightOver', 'fail')">返回</el-button>
      <el-button class="main" type="success" plain v-show="monsterCurHp === 0" @click="emits('fightOver')" style="margin-left: 0">返回</el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.person-fight {
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  .monster-info {
    :deep() {
      h2 {
        margin: 0;
      }
      button {
        margin-bottom: 10px;
      }
      .monster-detail {
        h3 {
          margin: 5px 0;
        }
        p {
          margin: 2px 0;
        }
      }
    }
  }
  .monster-extra-info {
    font-size: 12px;
    margin-top: 2px;
    display: flex;
    justify-content: space-between;
    padding: 0 5px;
  }
  .fight-over-timer {
    background-color: rgba($color: #fff, $alpha: 0.2);
    font-weight: bold;
    border-radius: 5px;
    padding: 5px 10px;
    margin: 0 auto;
    margin-bottom: 10px;
  }
  .fight-res-info-wrap {
    border: 2px var(--fontColor) solid;
    border-radius: 5px;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .fight-res-info {
      flex: 1;
      max-height: 100%;
      overflow: auto;
      padding: 0 20px 20px 20px;
      p {
        font-size: 14px;
        margin: 4px 0;
      }
    }
  }
  .fight-btn-wrap {
    margin: 10px 0;
    display: flex;
    .main {
      flex: 1;
    }
    .hour-list {
      display: flex;
      flex-flow: column nowrap;
      button {
        width: 100%;
        margin: 4px 0;
      }
    }
  }
}
</style>
